/* //eslint-disable react-hooks/exhaustive-deps */
//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useCallback, useState, useContext, useEffect } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { moderateUserTextInput } from '../../common/sightEngineHelper';
import useCountryStateCity from '../../common/hooks/useCountryStateCity';
// import { unsubscribeRealtimeUpdates } from '../system/firebase';
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import useRequiredFields from '../../common/hooks/useRequiredFields';
import {
    CONFIRM_DIALOG_MESSAGE_CLEAR_ALL,
    CONFIRM_DIALOG_TITLE_CLEAR_ALL,
    ERROR_MESSAGE_INVALID_YEAR,
    MESSAGE_ALL_REQUIRED_FIELDS,
    MESSAGE_INVALID_YEAR,
    SNACK_BAR_MESSAGE_TEXT_MODERATION_CHECK_FAILED,
} from '../../common/constants';
import {
    NO_OPTIONS_TEXT_INDUSTRY,
    PLACEHOLDER_FIELD_BENEFITS_SUMMARY,
    PLACEHOLDER_FIELD_COMPANY_BIO,
    PLACEHOLDER_FIELD_COMPANY_NAME,
    PLACEHOLDER_FIELD_INDUSTRY,
    PLACEHOLDER_FIELD_YEAR_FOUNDED,
} from '../../common/placeholderHelper';
import { ELASTICSEARCH_TYPE_INDUSTRY } from '../../common/elasticSearchHelper';
import { getCreatableLovSuggestions } from '../../common/lovHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { saveProfileCompanyInfo } from '../../common/services/empMyProfileServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdEditDialog from '../../common/elemental/AfitdEditDialog';
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdAutocompleteLiteCreatable from '../../common/elemental/AfitdAutocompleteLiteCreatable';
import AfitdNumberFormat from '../../common/elemental/AfitdNumberFormat';
import AfitdCountryAutocomplete from '../../common/elemental/AfitdCountryAutocomplete';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';
import AfitdMessageDialog from '../../common/elemental/AfitdMessageDialog';
import AfitdCompanySizeAutocomplete from '../../common/elemental/AfitdCompanySizeAutocomplete';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
// import SubComponent1Example from './SubComponent1Example';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: {},
    fieldLabel: {
        fontWeight: 'bold',
        // fontSize: '14px',
        // lineHeight: '16px',
        marginTop: 40,
        marginBottom: 5,
    },
    companyNameLabel: {
        fontWeight: 'bold',
        // fontSize: '14px',
        // lineHeight: '16px',
        marginTop: 10,
        marginBottom: 5,
    },
    textAreaCount: {
        float: 'right',
    },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        appContext,
        formData,
        // setFormData,
        handleChangeField,
        handleChangeLovField,
        handleChangeCompanySize,
        handleChangeYearFounded,
        handleClickSaveButton,
        handleCloseDialog,
        handleCloseConfirmClearAllDialog,
        handleClickConfirmClearAllDialogYes,
        handleClickClearAllButton,
        isInvalidYear,
        getYearFoundedHelperText,
        isConfirmClearAllDialogOpen,
        setIsConfirmClearAllDialogOpen,
        pageErrorDialogOpen,
        setPageErrorDialogOpen,
        isDirty,
        setIsDirty,
        clearFields,
        // invalidRequiredFields,
        // setInvalidRequiredFields,
        getFieldError,
        // clearInvalidRequiredField,
        // validateRequiredFields,
        // currentEmployer,
        // country,
        // setCountry,
        // state,
        // setState,
        // city,
        // setCity,
        // cityLocation,
        // setCityLocation,
        countryProps,
        stateProps,
        cityProps,
        // stateLabelProps,
        // countryLov,
        // stateLov,
        // cityLov,
        // lovs,
        industryOptions,
        setIndustryOptions,
        openBadWordsDialog,
        badWords,
        clearOutBadWords,
        saveLoading,
    } = params;

    const { open } = props;

    const { esToken, setEsToken } = appContext;

    const { companyName, industry, foundedYr, companySize } = formData;
    const badWordsList = badWords?.profanity?.join(', ');

    // const companySizeOptions = _get(lovs, 'companySizeLov', []);
    // const industryOptions = _get(lovs, 'industryLov', []);

    const defns = {
        root: {
            label: 'Company Info',
            open,
            isDirty,
            onClose: handleCloseDialog,
            actionButtonDefns: {
                clearAll: {
                    onClick: handleClickClearAllButton,
                },
                save: {
                    disabled: !isDirty,
                    onClick: handleClickSaveButton,
                    label: saveLoading ? (
                        <>
                            <CircularProgress color="#ffffff" size={20} />
                            &nbsp; Saving
                        </>
                    ) : undefined,
                },
            },
        },
        fieldLabel: {
            variant: 'body2',
        },
        companyNameLabel: {
            variant: 'body2',
        },
        companyName: {
            name: 'companyName',
            placeholder: PLACEHOLDER_FIELD_COMPANY_NAME,
            onChange: handleChangeField,
            value: companyName,
            error: getFieldError('companyName'),
        },
        industry: {
            noOptionsText: NO_OPTIONS_TEXT_INDUSTRY,
            name: 'industry',
            placeholder: PLACEHOLDER_FIELD_INDUSTRY,
            options: industryOptions,
            value: industry,
            onChange: handleChangeLovField.bind(null, { name: 'industry' }),
            error: getFieldError('industry'),
            onSuggestionsFetchRequested: async value => {
                if (value) {
                    const options = await getCreatableLovSuggestions({ type: ELASTICSEARCH_TYPE_INDUSTRY, keywords: value, esToken, setEsToken });
                    setIndustryOptions(options);
                } else setIndustryOptions([]);
            },
        },
        companySize: {
            name: 'companySize',
            // placeholder: PLACEHOLDER_FIELD_COMPANY_SIZE,
            // options: companySizeOptions,
            value: companySize,
            // getOptionLabel: option => _get(option, 'label'),
            onChange: handleChangeCompanySize.bind(null, {}),
            error: getFieldError('companySize'),
        },
        foundedYr: {
            name: 'foundedYr',
            placeholder: PLACEHOLDER_FIELD_YEAR_FOUNDED,
            helperText: getYearFoundedHelperText(),
            prefix: '',
            defaultValue: foundedYr,
            format: '####',
            allowNegative: false,
            onValueChange: handleChangeYearFounded,
            error: isInvalidYear,
        },
        country: { ...countryProps },
        state: { ...stateProps },
        city: { ...cityProps },
        companyBio: {
            name: 'bio',
            placeholder: PLACEHOLDER_FIELD_COMPANY_BIO,
            onChange: handleChangeField,
            value: formData.bio,
            multiline: true,
            rows: 4,
        },
        benefitsSummary: {
            name: 'benefit',
            placeholder: PLACEHOLDER_FIELD_BENEFITS_SUMMARY,
            onChange: handleChangeField,
            value: formData.benefit,
            multiline: true,
            rows: 4,
        },
        confirmClearAllDialog: {
            title: CONFIRM_DIALOG_TITLE_CLEAR_ALL,
            open: isConfirmClearAllDialogOpen,
            message: CONFIRM_DIALOG_MESSAGE_CLEAR_ALL,
            onConfirm: handleClickConfirmClearAllDialogYes,
            onClose: () => setIsConfirmClearAllDialogOpen(false),
        },
        confirmClearAllDialogCancelButton: {
            onClick: handleCloseConfirmClearAllDialog.bind(null, setIsConfirmClearAllDialogOpen),
        },
        confirmClearAllDialogOkButton: {
            autoFocus: true,
            onClick: handleClickConfirmClearAllDialogYes.bind(null, clearFields, setIsDirty, setIsConfirmClearAllDialogOpen),
        },
        pageErrorDialog: {
            message: isInvalidYear ? MESSAGE_INVALID_YEAR : MESSAGE_ALL_REQUIRED_FIELDS,
            open: pageErrorDialogOpen,
            onClose: () => setPageErrorDialogOpen(false),
        },
        badWordsDialog: {
            title: 'Inappropriate Text is not Allowed!',
            children: (
                <AfitdTypography>
                    The word(s): <b>{badWordsList} </b>
                    does not meet our community guidelines. Inappropriate content cannot be saved to this platform.
                </AfitdTypography>
            ),
            open: openBadWordsDialog,
            onClose: () => clearOutBadWords(),
        },
    };
    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const defaultormData = {
    companyName: '',
    industry: null,
    foundedYr: '',
    benefitsSummary: null,
    companyBio: null,
    companySize: null,
    benefit: '',
    bio: '',
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const CompanyInfoDialog = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();

    const appContext = useContext(AppContext);
    const { userId, employerId, currentEmployer, lovs } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { data, open, onClose } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [isDirty, setIsDirty] = useState(false);
    const [isConfirmClearAllDialogOpen, setIsConfirmClearAllDialogOpen] = useState(false);

    const [formData, setFormData] = useState({ ...defaultormData });
    const { invalidRequiredFields, setInvalidRequiredFields, getFieldError, clearInvalidRequiredField, validateRequiredFields } = useRequiredFields();
    const [pageErrorDialogOpen, setPageErrorDialogOpen] = useState(false);
    const [isInvalidYear, setIsInvalidYear] = useState(false);
    const [industryOptions, setIndustryOptions] = useState([]);
    const [openBadWordsDialog, setOpenBadWordsDialog] = useState(false);
    const [badWords, setBadWords] = useState({});
    const [saveLoading, setSaveLoading] = useState(false);

    //---------------------------------------------------------------------
    // useCountryStateCity hook
    //---------------------------------------------------------------------
    const {
        // countryLov,
        // stateLov,
        // setStateLov,
        // cityLov,
        // setCityLov,
        country,
        setCountry,
        state,
        setState,
        city,
        setCity,
        cityLocation,
        setCityLocation,
        newCity,
        // countryCurrency,
        setCountryStateCity,
        countryProps,
        stateProps,
        cityProps,
        // stateLabelProps,
    } = useCountryStateCity({
        defaultCountryLov: _get(lovs, 'countryLov', []),
        setIsDirty,
        invalidRequiredFields,
        setInvalidRequiredFields,
        clearInvalidRequiredField,
        getFieldError,
    });

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // init
    useEffect(() => {
        if (open === true) {
            setFormData({ ...data });
        }
    }, [open, data]);

    useEffect(() => {
        if (open === true) {
            setCountry(_get(currentEmployer, 'country', null));
            setState(_get(currentEmployer, 'state', null));
            setCity(_get(currentEmployer, 'city', null));
            setCityLocation(_get(currentEmployer, 'cityLocation', null));
        }
    }, [currentEmployer, open, setCountry, setState, setCity, setCityLocation]);

    useEffect(() => {
        if (open && data) {
            setCountryStateCity({
                country: _get(data, 'country'),
                state: _get(data, 'state'),
                city: _get(data, 'city'),
                cityLocation: _get(data, 'cityLocation'),
            });
        }
    }, [open, data, setCountryStateCity]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const companyName = _get(formData, 'companyName');
    const industry = _get(formData, 'industry');
    const companySize = _get(formData, 'companySize');
    // const bio = _get(formData, 'bio');
    // const benefit = _get(formData, 'benefit');

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const clearFields = useCallback(() => {
        setFormData({ ...defaultormData });
        setCountry(null);
        setState(null);
        setCity(null);
        setCityLocation(null);
    }, [setFormData, setCountry, setState, setCity, setCityLocation]);

    // field onChange
    const handleChangeField = useCallback(
        evt => {
            const { name, value } = evt.target;
            setFormData({ ...formData, [name]: value });
            setIsDirty(true);
            // clear error highlight
            clearInvalidRequiredField(name);
        },
        [formData, clearInvalidRequiredField],
    );

    // LOV field onChange
    const handleChangeLovField = useCallback(
        ({ name, invalidRequiredFields, setInvalidRequiredFields }, evt, option) => {
            setFormData({ ...formData, [name]: option });
            setIsDirty(true);
            // clear error highlight
            clearInvalidRequiredField(name);
        },
        [formData, clearInvalidRequiredField],
    );

    const handleChangeCompanySize = useCallback(
        ({ invalidRequiredFields, setInvalidRequiredFields }, evt, option) => {
            const companySizeMinVal = option?.minVal;
            setFormData({ ...formData, companySize: companySizeMinVal });
            setIsDirty(true);
            // clear error highlight
            clearInvalidRequiredField('companySize');
        },
        [formData, clearInvalidRequiredField],
    );

    const handleChangeYearFounded = useCallback(
        value => {
            const year = value.floatValue;
            const validYear = _isNil(year) || year > 999;
            if (validYear) {
                setFormData({ ...formData, foundedYr: year });
                setIsDirty(true);
                setIsInvalidYear(false);
            } else {
                setIsInvalidYear(true);
            }
            // const isExactChecked = isExactRateLocal === RADIO_EXACT;
            // const payload = getPayload(isExactChecked, rateMin, rateMaxValue);
            // validateRateRange(payload, setIsInvalidRange, setIsValid);
            // onChange && onChange(payload, 'rateMax', isInvalidYear);
        },
        [formData],
    );

    const getYearFoundedHelperText = useCallback(() => {
        return isInvalidYear ? ERROR_MESSAGE_INVALID_YEAR : '';
    }, [isInvalidYear]);

    // clean up and close dialog
    const closeDialog = useCallback(() => {
        setIsDirty(false); // reset dirty flag
        clearFields(); // clear all  fields when exit
        onClose(); // fire onClose event
    }, [clearFields, onClose]);

    // is the page valid?
    const getPageIsValid = useCallback(() => {
        const requiredFields = { companyName, country, state, city, industry, companySize };

        return validateRequiredFields(requiredFields) && !isInvalidYear;
    }, [companyName, country, state, city, industry, companySize, validateRequiredFields, isInvalidYear]);

    // save
    const handleClickSaveButton = async () => {
        const data = {
            ...formData,
            country,
            state,
            city,
            cityLocation,
            newCity,
        };
        const params = {
            userId,
            employerId,
            data,
            closeDialog,
            setSnackbarMessage,
            setSnackbarErrorMessage,
        };
        if (getPageIsValid()) {
            setSaveLoading(true);

            const data = {
                companyName,
                // bio: bio?.replace(/\r?\n|\r/g, ''),
                // benefit: benefit?.replace(/\r?\n|\r/g, ''),
            };
            const response = await moderateUserTextInput({ text: JSON.stringify(data) });

            if (!response) {
                setSnackbarErrorMessage(SNACK_BAR_MESSAGE_TEXT_MODERATION_CHECK_FAILED);
                setSaveLoading(false);
            } else if (response?.profanity?.length > 0 || response?.personal?.length > 0 || response?.link?.length > 0) {
                setOpenBadWordsDialog(true);
                setBadWords(response);
                setSaveLoading(false);
            } else {
                await saveProfileCompanyInfo(params);
                setSaveLoading(false);
            }
        } else {
            setPageErrorDialogOpen(true);
        }
    };

    const clearOutBadWords = async () => {
        // eslint-disable-next-line no-unused-expressions
        await badWords?.profanity?.forEach(badWord => {
            badWord = badWord.toLowerCase();

            const regEx = new RegExp(badWord, 'ig');

            if (companyName?.toLowerCase()?.includes(badWord)) {
                const filteredCompanyName = companyName?.replace(regEx, '');
                setFormData({ ...formData, companyName: filteredCompanyName });
            }

            // if (bio?.toLowerCase()?.includes(badWord)) {
            //     const filteredBio = bio?.replace(regEx, '');
            //     setFormData({ ...formData, bio: filteredBio });
            // }

            // if (benefit?.toLowerCase()?.includes(badWord)) {
            //     const filteredBenefit = benefit?.replace(regEx, '');
            //     setFormData({ ...formData, benefit: filteredBenefit });
            // }
        });
        setOpenBadWordsDialog(false);
    };

    // close dialog
    const handleCloseDialog = () => {
        closeDialog();
    };

    // clear all
    const handleCloseConfirmClearAllDialog = () => {
        setIsConfirmClearAllDialogOpen(false);
    };

    const handleClickConfirmClearAllDialogYes = () => {
        setIsConfirmClearAllDialogOpen(false);
        clearFields();
        setIsDirty(true);
    };

    const handleClickClearAllButton = () => {
        setIsConfirmClearAllDialogOpen(true);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({
        classes,
        props,
        appContext,
        formData,
        setFormData,
        handleChangeField,
        handleChangeLovField,
        handleChangeCompanySize,
        handleChangeYearFounded,
        handleClickSaveButton,
        handleCloseDialog,
        handleCloseConfirmClearAllDialog,
        handleClickConfirmClearAllDialogYes,
        handleClickClearAllButton,
        isInvalidYear,
        getYearFoundedHelperText,
        isConfirmClearAllDialogOpen,
        setIsConfirmClearAllDialogOpen,
        pageErrorDialogOpen,
        setPageErrorDialogOpen,
        isDirty,
        setIsDirty,
        clearFields,
        invalidRequiredFields,
        setInvalidRequiredFields,
        getFieldError,
        clearInvalidRequiredField,
        validateRequiredFields,
        currentEmployer,
        // country,
        // setCountry,
        // state,
        // setState,
        // city,
        // setCity,
        // cityLocation,
        // setCityLocation,
        countryProps,
        stateProps,
        cityProps,
        // stateLabelProps,
        // countryLov,
        // stateLov,
        // cityLov,
        // lovs,
        industryOptions,
        setIndustryOptions,
        openBadWordsDialog,
        setOpenBadWordsDialog,
        badWords,
        setBadWords,
        clearOutBadWords,
        saveLoading,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------

    return (
        <AfitdEditDialog {...defns.root}>
            <Grid container>
                <Grid item xs={12}>
                    <AfitdTypography {...defns.companyNameLabel}>Company Name: *</AfitdTypography>
                    <AfitdTextField {...defns.companyName} />
                </Grid>
            </Grid>
            <Grid container justifyContent="space-between" spacing={1}>
                <Grid item xs={6}>
                    <AfitdTypography {...defns.fieldLabel}>Industry: *</AfitdTypography>
                    <AfitdAutocompleteLiteCreatable {...defns.industry} />
                </Grid>
                <Grid item xs={6}>
                    <AfitdTypography {...defns.fieldLabel}>Founded:</AfitdTypography>
                    <AfitdNumberFormat customInput={AfitdTextField} {...defns.foundedYr} />
                </Grid>
            </Grid>
            <Grid container spacing={1}>
                <Grid item xs={6}>
                    <AfitdTypography {...defns.fieldLabel}>Company Size: *</AfitdTypography>
                    <AfitdCompanySizeAutocomplete {...defns.companySize} />
                </Grid>
            </Grid>
            <AfitdTypography {...defns.fieldLabel}>Headquarters/Head Office Location: *</AfitdTypography>
            <Grid container spacing={1}>
                <Grid item xs={6}>
                    <AfitdCountryAutocomplete {...defns.country} />
                </Grid>
                <Grid item xs={6}>
                    <AfitdCountryAutocomplete {...defns.state} />
                </Grid>
                <Grid item xs={6}>
                    <AfitdCountryAutocomplete {...defns.city} />
                </Grid>
            </Grid>
            <Grid container>
                <Grid item xs={12}>
                    <AfitdTypography {...defns.fieldLabel}>Company Bio:</AfitdTypography>
                    <AfitdTextField {...defns.companyBio} />
                </Grid>
            </Grid>
            <Grid container>
                <Grid item xs={12}>
                    <AfitdTypography {...defns.fieldLabel}>Benefits Summary:</AfitdTypography>
                    <AfitdTextField {...defns.benefitsSummary} />
                </Grid>
            </Grid>
            <AfitdConfirmDialog {...defns.confirmClearAllDialog} />
            <AfitdMessageDialog {...defns.pageErrorDialog} />
            <AfitdMessageDialog {...defns.badWordsDialog} />
        </AfitdEditDialog>
    );
};

export default CompanyInfoDialog;
